<template>
  <div class="grid grid-cols-2 gap-4 mb-4">
    <div class="bg-white border border-gray-200 p-3">
      <div class="font-medium">Ticket flow</div>
      <div class="text-gray-500">Weekly tickets entering versus leaving the backlog.</div>
      <p v-if="flowError" class="text-red-800 py-8">Chart failed: {{ flowError }}</p>
      <p v-else-if="!flow.length" class="text-gray-500 py-8">No ticket movement in the last 12 weeks.</p>
      <div v-show="flow.length && !flowError" style="height: 220px"><canvas ref="flowCanvas"></canvas></div>
    </div>

    <div class="bg-white border border-gray-200 p-3">
      <div class="flex justify-between">
        <div>
          <div class="font-medium">Engineering work</div>
          <div class="text-gray-500">Daily active pull requests for one engineer.</div>
        </div>
        <select v-model="engineer" class="border border-gray-300 p-1">
          <option v-for="name in engineers" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <p v-if="workError" class="text-red-800 py-8">Chart failed: {{ workError }}</p>
      <p v-else-if="!engineers.length" class="text-gray-500 py-8">No classified pull requests yet.</p>
      <div v-show="engineers.length && !workError" style="height: 220px"><canvas ref="workCanvas"></canvas></div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { CATEGORY_LABELS, label } from './hades_constants';

// Customers get a stable colour so a bar means the same thing week to week.
// Assigned by sorted position, not by iteration order, or the palette shuffles
// whenever a customer's ticket count changes.
const CUSTOMER_COLORS = ['#F76734', '#4C6E97', '#88A484', '#F1BD6C', '#8E8F90', '#B7ACFC'];
const CATEGORY_COLORS = {
  customer_feature: '#F76734',
  bug_fix: '#D53D3D',
  app_stability_reliability: '#4C6E97',
  architecture_platform: '#88A484',
  data_integration: '#F1BD6C',
  security_compliance: '#8D84E8',
  internal_tooling: '#A5A5A6',
  documentation: '#D2D2D3',
  maintenance: '#D2D2D3',
  unclassified: '#E8E9E9',
};

export default {
  name: 'HadesCharts',
  props: {
    flow: { type: Array, required: true },
    pullRequests: { type: Array, required: true },
    customer: { type: String, default: null },
  },
  data() {
    return { engineer: null, flowChart: null, workChart: null,
      flowError: null, workError: null };
  },
  computed: {
    engineers() {
      return [...new Set(this.pullRequests
        .map((p) => p.engineer_id || p.author_login)
        .filter(Boolean))].sort();
    },
    weeks() {
      return [...new Set(this.flow.map((r) => r.week_start))].sort();
    },
    customers() {
      if (this.customer) return [this.customer];
      return [...new Set(this.flow.map((r) => r.customer))].sort();
    },
  },
  watch: {
    flow: 'renderFlow',
    customer: 'renderAll',
    pullRequests: 'renderAll',
    engineer: 'renderWork',
  },
  mounted() {
    this.$nextTick(this.renderAll);
  },
  beforeUnmount() {
    this.flowChart?.destroy();
    this.workChart?.destroy();
  },
  methods: {
    renderAll() {
      if (!this.engineer || !this.engineers.includes(this.engineer)) {
        this.engineer = this.engineers[0] || null;
      }
      this.$nextTick(() => {
        this.renderFlow();
        this.renderWork();
      });
    },
    dayLabel(iso) {
      return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined,
        { month: 'short', day: 'numeric' });
    },
    renderFlow() {
      try {
        this.drawFlow();
      } catch (e) {
        this.flowError = e.message;
         
        console.error('Hades ticket flow failed to render', e);
      }
    },
    drawFlow() {
      this.flowError = null;
      this.flowChart?.destroy();
      const canvas = this.$refs.flowCanvas;
      if (!canvas) return;

      const weeks = this.weeks;
      const rows = this.flow.filter((r) => !this.customer || r.customer === this.customer);
      const at = {};
      rows.forEach((r) => { at[`${r.week_start}|${r.customer}`] = r; });

      // NEW above the line, CLOSED below it, both stacked by customer. One
      // gesture answers "is the queue growing" — two adjacent charts do not.
      const datasets = [];
      this.customers.forEach((name, i) => {
        const colour = CUSTOMER_COLORS[i % CUSTOMER_COLORS.length];
        datasets.push({
          label: `${name} — New`, customer: name, direction: 'new',
          stack: 'incoming', backgroundColor: colour, borderWidth: 0,
          data: weeks.map((w) => +(at[`${w}|${name}`]?.incoming || 0)),
        });
        datasets.push({
          label: `${name} — Closed`, customer: name, direction: 'closed',
          stack: 'outgoing', backgroundColor: colour, borderWidth: 0,
          data: weeks.map((w) => -(at[`${w}|${name}`]?.outgoing || 0)),
        });
      });

      this.flowChart = new Chart(canvas, {
        type: 'bar',
        data: { labels: weeks.map(this.dayLabel), datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              filter: (item) => item.raw !== 0,
              callbacks: {
                label: (item) => `${item.dataset.customer}: `
                  + `${Math.abs(item.raw)} ${item.dataset.direction}`,
                footer: (items) => {
                  const i = items[0].dataIndex;
                  const sum = (dir) => datasets
                    .filter((d) => d.direction === dir)
                    .reduce((n, d) => n + d.data[i], 0);
                  const net = sum('new') + sum('closed');
                  return `Net backlog ${net >= 0 ? '+' : ''}${net}`;
                },
              },
            },
          },
          scales: {
            x: { stacked: true, grid: { display: false } },
            // Ticks render ABS, or the outflow side reads as negative work.
            y: { stacked: true, ticks: { callback: (v) => Math.abs(v) } },
          },
        },
      });
    },
    renderWork() {
      try {
        this.drawWork();
      } catch (e) {
        this.workError = e.message;
         
        console.error('Hades engineering work failed to render', e);
      }
    },
    drawWork() {
      this.workError = null;
      this.workChart?.destroy();
      const canvas = this.$refs.workCanvas;
      if (!canvas || !this.engineer) return;

      const mine = this.pullRequests.filter(
        (p) => (p.engineer_id || p.author_login) === this.engineer);
      const days = this.days();
      const categories = [...new Set(mine.map((p) => p.primary_category))];

      // A PR counts on every day it was OPEN, not just the day it merged — the
      // question is what someone was working on, not what landed.
      //
      // created_at is the PULL REQUEST's open date — api_hadespr is a plain
      // models.Model, so nothing auto-stamps it and the store writes the real
      // GitHub date. Rows written before that fix all carry the insert time, so
      // they collapse onto one day until a fleet run rewrites them.
      const activeOn = (day, category) => mine.filter((p) => {
        if (p.primary_category !== category) return false;
        const opened = (p.created_at || p.merged_at || '').slice(0, 10);
        if (!opened) return false;
        const closed = p.merged_at ? p.merged_at.slice(0, 10) : day;
        return opened <= day && day <= closed;
      });

      this.workChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: days.map(this.dayLabel),
          datasets: categories.map((category) => ({
            label: label(CATEGORY_LABELS, category),
            backgroundColor: CATEGORY_COLORS[category] || '#D2D2D3',
            borderWidth: 0,
            stack: 'work',
            data: days.map((d) => activeOn(d, category).length),
            refs: days.map((d) => activeOn(d, category)
              .map((p) => `${p.repository}#${p.pr_number}`)),
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'top', labels: { boxWidth: 10, boxHeight: 10 } },
            tooltip: {
              filter: (item) => item.raw !== 0,
              callbacks: {
                afterLabel: (item) => item.dataset.refs[item.dataIndex].slice(0, 5),
              },
            },
          },
          scales: {
            x: { stacked: true, grid: { display: false },
                 ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 0 } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },
    days() {
      // From end of July, per the original spec.
      const out = [];
      const day = new Date('2026-07-27T00:00:00Z');
      const today = new Date();
      while (day <= today) {
        out.push(day.toISOString().slice(0, 10));
        day.setUTCDate(day.getUTCDate() + 1);
      }
      return out;
    },
  },
};
</script>
